/**
 * Billing Service
 * Wrapper around existing API clients for billing operations
 * Provides a unified interface for invoices, quotes, credit notes, and payment schedules
 */

import { invoicesAPI, quotesAPI } from '@/utils/api';
import { creditNotesAPI } from '@/utils/creditNotesAPI';
import type { CreateCreditNoteDTO as ApiCreateCreditNoteDTO, UpdateCreditNoteDTO as ApiUpdateCreditNoteDTO } from '@/utils/creditNotesAPI';
import { paymentSchedulesAPI } from '@/utils/paymentSchedulesAPI';
import type { PaymentScheduleStats as ApiPaymentScheduleStats } from '@/utils/paymentSchedulesAPI';
import { recurringInvoicesAPI } from '@/utils/recurringInvoicesAPI';
import { quoteVariantsAPI } from '@/utils/quoteVariantsAPI';
import type { Quote as ModelsQuote } from '@/types/models';
import type {
  Invoice,
  Quote,
  CreditNote,
  PaymentSchedule,
  RecurringInvoice,
  RecurringInvoiceStats,
  CreateInvoiceDTO,
  UpdateInvoiceDTO,
  CreateQuoteDTO,
  UpdateQuoteDTO,
  CreateCreditNoteDTO,
  UpdateCreditNoteDTO,
  CreateRecurringInvoiceDTO,
  UpdateRecurringInvoiceDTO,
  CreateScheduleParams,
  UpdateScheduleParams,
  GenerateSchedulesParams,
  InvoicesResponse,
  QuotesResponse,
  CreditNotesResponse,
  RecurringInvoicesResponse,
  PaymentScheduleStats,
  InvoiceStatus,
  QuoteStatus,
  CreditNoteStatus,
  RecurringInvoiceStatus,
  QuoteVariant,
  CreateQuoteVariantDTO,
  UpdateQuoteVariantDTO,
  GenerateVariantsOptions,
} from '../types/billing.types';

/**
 * Invoices Service
 */
export const invoicesService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: InvoiceStatus;
  }): Promise<InvoicesResponse> => {
    // invoicesAPI.getAll fetches all invoices; apply client-side pagination
    const allInvoices = await invoicesAPI.getAll();
    const page = params?.page ?? 1;
    const limit = params?.limit ?? allInvoices.length;
    const filtered = params?.status
      ? allInvoices.filter((inv: Invoice) => inv.status === params.status)
      : allInvoices;
    const total = filtered.length;
    const pages = limit > 0 ? Math.ceil(total / limit) : 1;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);
    return {
      invoices: paginated,
      pagination: {
        total,
        page,
        limit,
        pages,
      },
    };
  },

  getById: async (id: string): Promise<Invoice> => {
    return await invoicesAPI.getById(id);
  },

  create: async (data: CreateInvoiceDTO): Promise<Invoice> => {
    return await invoicesAPI.create(data);
  },

  update: async (id: string, data: UpdateInvoiceDTO): Promise<Invoice> => {
    return await invoicesAPI.update(id, data);
  },

  updateStatus: async (id: string, status: InvoiceStatus): Promise<Invoice> => {
    return await invoicesAPI.updateStatus(id, status);
  },

  markAsPaid: async (id: string, _paymentDate?: string): Promise<Invoice> => {
    // Note: invoicesAPI.markAsPaid expects paidAmount (number), not paymentDate
    // The API internally sets status to PAID, so we pass undefined for paidAmount
    return await invoicesAPI.markAsPaid(id, undefined);
  },

  delete: async (id: string): Promise<void> => {
    return await invoicesAPI.delete(id);
  },

  send: async (id: string, email: string): Promise<void> => {
    return await invoicesAPI.send(id, email);
  },

  downloadPDF: async (id: string): Promise<Blob> => {
    return await invoicesAPI.downloadPDF(id);
  },

  getStats: async (): Promise<{
    totalCount: number;
    totalAmount: number;
    byStatus: Record<InvoiceStatus, { count: number; amount: number }>;
  }> => {
    return await invoicesAPI.getStats();
  },
};

/**
 * Quotes Service
 */
export const quotesService = {
  getAll: async (_params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: QuoteStatus;
  }): Promise<QuotesResponse> => {
    // Note: quotesAPI.getAll doesn't accept params, filtering should be done client-side
    const quotes = await quotesAPI.getAll();
    return {
      quotes,
      pagination: {
        total: quotes.length,
        page: 1,
        limit: quotes.length,
        pages: 1,
      },
    };
  },

  getById: async (id: string): Promise<Quote> => {
    return await quotesAPI.getById(id);
  },

  create: async (data: CreateQuoteDTO): Promise<Quote> => {
    // Cast to Partial<ModelsQuote> as quotesAPI.create expects that type from @/types/models
    const quoteData: Partial<ModelsQuote> = data as unknown as Partial<ModelsQuote>;
    return await quotesAPI.create(quoteData);
  },

  update: async (id: string, data: UpdateQuoteDTO): Promise<Quote> => {
    const quoteData: Partial<ModelsQuote> = data as unknown as Partial<ModelsQuote>;
    return await quotesAPI.update(id, quoteData);
  },

  updateStatus: async (id: string, status: QuoteStatus): Promise<Quote> => {
    return await quotesAPI.updateStatus(id, status);
  },

  accept: async (id: string): Promise<Quote> => {
    return await quotesAPI.accept(id);
  },

  reject: async (id: string): Promise<Quote> => {
    return await quotesAPI.reject(id);
  },

  convertToInvoice: async (id: string): Promise<Invoice> => {
    return await quotesAPI.convertToInvoice(id);
  },

  delete: async (id: string): Promise<void> => {
    return await quotesAPI.delete(id);
  },

  send: async (id: string, email: string): Promise<void> => {
    return await quotesAPI.send(id, email);
  },

  downloadPDF: async (id: string): Promise<Blob> => {
    return await quotesAPI.downloadPDF(id);
  },

  getStats: async (): Promise<{
    totalCount: number;
    totalAmount: number;
    byStatus: Record<QuoteStatus, { count: number; amount: number }>;
  }> => {
    return await quotesAPI.getStats();
  },
};

/**
 * Credit Notes Service
 */
export const creditNotesService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: CreditNoteStatus;
    invoiceId?: string;
  }): Promise<CreditNotesResponse> => {
    return await creditNotesAPI.getAll(params);
  },

  getById: async (id: string): Promise<CreditNote> => {
    return await creditNotesAPI.getById(id);
  },

  getByInvoice: async (invoiceId: string): Promise<CreditNote[]> => {
    return await creditNotesAPI.getByInvoice(invoiceId);
  },

  create: async (data: CreateCreditNoteDTO): Promise<CreditNote> => {
    // Cast to API DTO type - API requires taxRateId and position as required in items
    return await creditNotesAPI.create(data as unknown as ApiCreateCreditNoteDTO);
  },

  update: async (id: string, data: UpdateCreditNoteDTO): Promise<CreditNote> => {
    // Cast to API DTO type - API requires taxRateId as required in items
    return await creditNotesAPI.update(id, data as unknown as ApiUpdateCreditNoteDTO);
  },

  updateStatus: async (id: string, status: CreditNoteStatus): Promise<CreditNote> => {
    return await creditNotesAPI.updateStatus(id, status);
  },

  apply: async (id: string): Promise<CreditNote> => {
    return await creditNotesAPI.apply(id);
  },

  delete: async (id: string): Promise<void> => {
    return await creditNotesAPI.delete(id);
  },

  getStats: async (): Promise<{
    totalCount: number;
    totalAmount: number;
    byStatus: Record<CreditNoteStatus, { count: number; amount: number }>;
  }> => {
    return await creditNotesAPI.getStats();
  },
};

/**
 * Payment Schedules Service
 */
export const paymentSchedulesService = {
  getByInvoice: async (invoiceId: string): Promise<PaymentSchedule[]> => {
    return await paymentSchedulesAPI.getByInvoice(invoiceId);
  },

  getUpcoming: async (days: number = 30): Promise<PaymentSchedule[]> => {
    return await paymentSchedulesAPI.getUpcoming(days);
  },

  getById: async (scheduleId: string): Promise<PaymentSchedule> => {
    return await paymentSchedulesAPI.getById(scheduleId);
  },

  getStats: async (invoiceId: string): Promise<PaymentScheduleStats> => {
    const apiStats: ApiPaymentScheduleStats = await paymentSchedulesAPI.getStats(invoiceId);
    // Transform API response to match PaymentScheduleStats interface
    return {
      total: apiStats.totalCount,
      pending: apiStats.pending.count,
      paid: apiStats.paid.count,
      overdue: apiStats.overdue.count,
      totalAmount: apiStats.pending.total + apiStats.paid.total + apiStats.overdue.total,
      paidAmount: apiStats.paidAmount,
      remainingAmount: apiStats.remainingAmount,
    };
  },

  create: async (params: CreateScheduleParams): Promise<PaymentSchedule> => {
    return await paymentSchedulesAPI.create(params);
  },

  generate: async (
    invoiceId: string,
    params: GenerateSchedulesParams
  ): Promise<PaymentSchedule[]> => {
    return await paymentSchedulesAPI.generate(invoiceId, params);
  },

  update: async (
    scheduleId: string,
    params: UpdateScheduleParams
  ): Promise<PaymentSchedule> => {
    return await paymentSchedulesAPI.update(scheduleId, params);
  },

  markAsPaid: async (
    scheduleId: string,
    paidAt?: string
  ): Promise<PaymentSchedule> => {
    return await paymentSchedulesAPI.markAsPaid(scheduleId, paidAt);
  },

  delete: async (scheduleId: string): Promise<{ success: boolean }> => {
    return await paymentSchedulesAPI.delete(scheduleId);
  },
};

/**
 * Recurring Invoices Service
 */
export const recurringInvoicesService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: RecurringInvoiceStatus;
  }): Promise<RecurringInvoicesResponse> => {
    return await recurringInvoicesAPI.getAll(params);
  },

  getById: async (id: string): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.getById(id);
  },

  create: async (data: CreateRecurringInvoiceDTO): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.create(data);
  },

  update: async (id: string, data: UpdateRecurringInvoiceDTO): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.update(id, data);
  },

  pause: async (id: string): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.pause(id);
  },

  resume: async (id: string): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.resume(id);
  },

  cancel: async (id: string): Promise<RecurringInvoice> => {
    return await recurringInvoicesAPI.cancel(id);
  },

  generateInvoice: async (id: string): Promise<Invoice> => {
    return await recurringInvoicesAPI.generateInvoice(id);
  },

  delete: async (id: string): Promise<void> => {
    return await recurringInvoicesAPI.delete(id);
  },

  getStats: async (): Promise<RecurringInvoiceStats> => {
    return await recurringInvoicesAPI.getStats();
  },
};

/**
 * Quote Variants Service (TASK-302)
 */
export const quoteVariantsService = {
  getByQuote: async (quoteId: string): Promise<QuoteVariant[]> => {
    return await quoteVariantsAPI.getByQuote(quoteId);
  },

  getById: async (quoteId: string, variantId: string): Promise<QuoteVariant> => {
    return await quoteVariantsAPI.getById(quoteId, variantId);
  },

  create: async (quoteId: string, data: CreateQuoteVariantDTO): Promise<QuoteVariant> => {
    return await quoteVariantsAPI.create(quoteId, data);
  },

  update: async (quoteId: string, variantId: string, data: UpdateQuoteVariantDTO): Promise<QuoteVariant> => {
    return await quoteVariantsAPI.update(quoteId, variantId, data);
  },

  delete: async (quoteId: string, variantId: string): Promise<void> => {
    return await quoteVariantsAPI.delete(quoteId, variantId);
  },

  setDefault: async (quoteId: string, variantId: string): Promise<void> => {
    return await quoteVariantsAPI.setDefault(quoteId, variantId);
  },

  select: async (quoteId: string, variantId: string): Promise<void> => {
    return await quoteVariantsAPI.select(quoteId, variantId);
  },

  duplicate: async (quoteId: string, variantId: string, newLabel?: string): Promise<QuoteVariant> => {
    return await quoteVariantsAPI.duplicate(quoteId, variantId, newLabel);
  },

  generateDefaults: async (quoteId: string, options?: GenerateVariantsOptions): Promise<QuoteVariant[]> => {
    return await quoteVariantsAPI.generateDefaults(quoteId, options);
  },
};

/**
 * Unified Billing Service
 * Combines all billing-related services
 */
export const billingService = {
  invoices: invoicesService,
  quotes: quotesService,
  creditNotes: creditNotesService,
  paymentSchedules: paymentSchedulesService,
  recurringInvoices: recurringInvoicesService,
  quoteVariants: quoteVariantsService,
};
